import { Check, Loader, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { API_URL } from '../config/api'

interface InvitationData {
  projectId: number
  projectTitle: string
  projectDescription: string
  inviterName: string
  expiresAt: string
}

export default function InvitationAccept() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link')
      setLoading(false)
      return
    }

    const fetchInvitation = async () => {
      try {
        const response = await fetch(`${API_URL}/api/invitations/${token}`)
        if (!response.ok) { throw new Error('Invitation not found or expired') }
        const data: InvitationData = await response.json() as InvitationData
        setInvitation(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load invitation')
      } finally {
        setLoading(false)
      }
    }

    void fetchInvitation()
  }, [token])

  const handleAccept = async () => {
    if (!token) { return }
    setAccepting(true)
    setError('')
    try {
      const response = await fetch(`${API_URL}/api/invitations/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      })
      if (!response.ok) { throw new Error('Could not accept invitation') }
      setSuccess(true)
      setTimeout(() => {
        if (invitation) { navigate(`/projects/${invitation.projectId}`) }
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept invitation')
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader size={40} className="animate-spin mx-auto mb-4" style={{ color: 'var(--primary-color)' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
            <X size={32} className="text-red-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Invalid Invitation</h1>
          <p className="text-base mb-6" style={{ color: 'var(--text-secondary)' }}>{error}</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 rounded-lg font-medium"
            style={{ background: 'var(--primary-color)', color: '#fff' }}>
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
            <Check size={32} className="text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Invitation Accepted!</h1>
          <p className="text-base mb-6" style={{ color: 'var(--text-secondary)' }}>Redirecting to project...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Project Invitation</h1>
          <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
            {invitation?.inviterName} invited you to their project
          </p>
        </div>

        <div className="rounded-lg p-6 mb-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <h2 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{invitation?.projectTitle}</h2>
          <p className="text-base mb-4" style={{ color: 'var(--text-primary)' }}>{invitation?.projectDescription}</p>
          {invitation?.expiresAt && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Expires: {new Date(invitation.expiresAt).toLocaleDateString('en-US')}
            </p>
          )}
        </div>

        {error && (
          <div className="rounded-lg p-4 mb-4" style={{ background: '#ef444410', border: '1px solid #ef444430' }}>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={() => navigate('/')} disabled={accepting} className="flex-1 py-3 px-4 rounded-lg font-medium"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            Decline
          </button>
          <button onClick={() => void handleAccept()} disabled={accepting} className="flex-1 py-3 px-4 rounded-lg font-medium"
            style={{ background: accepting ? 'var(--text-secondary)' : 'var(--primary-color)', color: '#fff' }}>
            {accepting ? 'Accepting...' : 'Accept'}
          </button>
        </div>

        <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>By accepting you can:</p>
          <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li>• View project details</li>
            <li>• Contribute to the project</li>
            <li>• Join project chat</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
